import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/* ─── CSS injected once ──────────────────────────────────────────────────── */
const CHAT_CSS = `
@keyframes orb-pulse {
  0%,100% { box-shadow: 0 0 0 0 rgba(201,151,58,0.6); opacity:1; }
  50%      { box-shadow: 0 0 0 8px rgba(201,151,58,0); opacity:0.8; }
}
@keyframes orb-spin {
  to { transform: rotate(360deg); }
}
@keyframes thinking-dots {
  0%,20%  { opacity:0.2; transform:translateY(0); }
  50%     { opacity:1;   transform:translateY(-3px); }
  80%,100%{ opacity:0.2; transform:translateY(0); }
}
@keyframes tool-slide-in {
  from { opacity:0; transform:translateX(-8px); }
  to   { opacity:1; transform:translateX(0); }
}
@keyframes artifact-pop {
  0%   { opacity:0; transform:scale(0.97) translateY(6px); }
  100% { opacity:1; transform:scale(1)    translateY(0); }
}
@keyframes cursor-blink {
  0%,100% { opacity:1; }
  50%     { opacity:0; }
}
@keyframes progress-stripe {
  from { background-position: 0 0; }
  to   { background-position: 30px 0; }
}
.chat-orb {
  width:32px; height:32px; border-radius:50%; flex-shrink:0;
  background: conic-gradient(var(--gold) 0deg, var(--navy-5) 120deg, var(--gold-2) 240deg, var(--navy-5) 360deg);
  border:2px solid var(--gold-border);
  display:flex; align-items:center; justify-content:center;
  font-size:13px; position:relative; transition:all 0.3s;
}
.chat-orb.idle {
  background: linear-gradient(135deg, var(--navy-4), var(--navy-5));
  border-color: var(--border2);
}
.chat-orb.streaming {
  animation: orb-spin 2s linear infinite;
  box-shadow: 0 0 16px rgba(201,151,58,0.3);
}
.chat-orb.thinking {
  animation: orb-pulse 1.5s ease-in-out infinite;
  background: linear-gradient(135deg, #7c3aed, #4c1d95);
  border-color: rgba(124,58,237,0.5);
}
.thinking-dot {
  width:5px; height:5px; border-radius:50%; background:currentColor; display:inline-block;
}
.thinking-dot:nth-child(1) { animation: thinking-dots 1.2s ease-in-out infinite 0s; }
.thinking-dot:nth-child(2) { animation: thinking-dots 1.2s ease-in-out infinite 0.2s; }
.thinking-dot:nth-child(3) { animation: thinking-dots 1.2s ease-in-out infinite 0.4s; }
.tool-status-chip {
  display:inline-flex; align-items:center; gap:7px;
  padding:6px 12px;
  background:var(--bg-elevated);
  border:1px solid var(--border-2); border-radius:20px;
  font-size:12px; color:var(--blue);
  animation: tool-slide-in 0.2s ease both;
}
.tool-progress {
  height:2px; background:var(--bg4); border-radius:1px; overflow:hidden;
  margin-top:6px; width:180px;
}
.tool-progress-fill {
  height:100%; border-radius:1px;
  background:linear-gradient(90deg, var(--gold), var(--gold-2), var(--gold));
  background-size:200% 100%;
  animation:progress-stripe 1.5s linear infinite;
}
.artifact-card {
  margin:10px 0; border-radius:10px; overflow:hidden;
  animation: artifact-pop 0.25s cubic-bezier(0.34,1.56,0.64,1) both;
}
.html-artifact-frame {
  width:100%; border:none; background:#fff; display:block;
  transition:height 0.3s ease;
}
.file-artifact {
  display:flex; align-items:center; justify-content:space-between;
  gap:12px; padding:12px 16px; flex-wrap:wrap;
  background:rgba(46,125,92,0.07); border:1px solid rgba(46,125,92,0.25);
}
.msg-act-btn {
  background:none; border:none; cursor:pointer; color:var(--text3);
  font-size:11px; padding:4px 8px; border-radius:6px;
  display:inline-flex; align-items:center; gap:5px;
  transition:all 0.12s; font-family:inherit;
}
.msg-act-btn:hover { background:var(--bg-elevated); color:var(--text2); }
.send-btn {
  display:inline-flex; align-items:center; gap:6px;
  padding:8px 18px; border-radius:8px; font-size:13px; font-weight:600;
  cursor:pointer; font-family:inherit; transition:all 0.15s;
  border:none;
}
.send-btn.ready {
  background:var(--blue); color:#fff;
  box-shadow:0 2px 8px rgba(37,99,235,0.3);
}
.send-btn.ready:hover { background:var(--blue-h); box-shadow:0 4px 16px rgba(37,99,235,0.35); transform:translateY(-1px); }
.send-btn.disabled { background:var(--bg-elevated); color:var(--text-3); cursor:not-allowed; border:1px solid var(--border-2); }
.send-btn.streaming { background:var(--red-soft); color:var(--red); border:1px solid var(--red-border); }
.send-btn.streaming:hover { background:var(--red-soft); filter:brightness(0.9); }
.think-btn {
  padding:8px 12px; border-radius:8px; font-size:12px; font-weight:500;
  cursor:pointer; font-family:inherit; transition:all 0.15s;
  background:var(--purple-soft); border:1px solid var(--purple-border); color:var(--purple);
}
.think-btn:hover { background:var(--purple-soft); filter:brightness(0.9); }
.think-btn:disabled { opacity:0.4; cursor:not-allowed; }
.input-area {
  background:var(--bg3); border:1px solid var(--border2); border-radius:12px;
  padding:12px 14px; display:flex; flex-direction:column; gap:8px;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.input-area:focus-within {
  border-color:var(--gold-border); box-shadow:0 0 0 3px var(--gold-soft);
}
.input-area.streaming-active {
  border-color:var(--blue-border);
  box-shadow:0 0 0 3px var(--blue-soft);
}
.suggestion-chip {
  padding:9px 14px; background:var(--bg3); border:1px solid var(--border);
  border-radius:9px; color:var(--text2); font-size:12px; text-align:left;
  cursor:pointer; line-height:1.4; transition:all 0.15s; font-family:inherit;
}
.suggestion-chip:hover { border-color:var(--gold-border); color:var(--text); background:var(--gold-soft); }
`;

/* ─── Markdown renderer ────────────────────────────────────────────────── */
function inl(text) {
  if (!text) return null;
  const parts = []; let last = 0;
  const re = /(\*\*(.+?)\*\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\))/g;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[2]) parts.push(<strong key={m.index} style={{color:'var(--text)',fontWeight:600}}>{m[2]}</strong>);
    else if (m[3]) parts.push(<code key={m.index} style={{fontFamily:'var(--ff-mono)',fontSize:'0.85em',background:'var(--bg-elevated)',padding:'1px 5px',borderRadius:4,color:'var(--blue)'}}>{m[3]}</code>);
    else if (m[4]) parts.push(<a key={m.index} href={m[5]} target="_blank" rel="noreferrer" style={{color:'var(--blue)',textDecoration:'underline'}}>{m[4]}</a>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : parts;
}

function hl(code, lang) {
  const esc = s => s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  let s = esc(code);
  s = s.replace(/(&quot;|&#x27;|`)([\s\S]*?)\1/g, m => `<span style="color:#a5d6ff">${m}</span>`);
  s = s.replace(/(\/\/[^\n]*|\/\*[\s\S]*?\*\/|#[^\n]*)/g, m => `<span style="color:#6e7681;font-style:italic">${m}</span>`);
  s = s.replace(/\b(\d+\.?\d*)\b/g, m => `<span style="color:#79c0ff">${m}</span>`);
  const kws = 'const|let|var|function|return|if|else|for|while|class|import|export|default|from|async|await|try|catch|new|typeof|null|undefined|true|false|def|print|elif|pass|None|True|False|SELECT|FROM|WHERE|INSERT|UPDATE|DELETE|CREATE|TABLE|JOIN';
  s = s.replace(new RegExp(`\\b(${kws})\\b`,'g'), m => `<span style="color:#ff7b72">${m}</span>`);
  s = s.replace(/\b([a-zA-Z_]\w*)(?=\s*\()/g, m => `<span style="color:#d2a8ff">${m}</span>`);
  return s;
}

function Markdown({ content, onPreview }) {
  if (!content) return null;
  const lines = content.split('\n');
  const out = []; let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    // Fenced code
    const fm = line.match(/^```(\w*)/);
    if (fm) {
      const lang = fm[1]; const codeLines = []; i++;
      while (i < lines.length && !lines[i].startsWith('```')) { codeLines.push(lines[i]); i++; }
      const code = codeLines.join('\n');
      const [copied, setCopied] = [false, () => {}]; // static for SSR
      out.push(
        <div key={`cb${i}`} style={{margin:'10px 0',borderRadius:8,overflow:'hidden',border:'1px solid var(--border)'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 14px',background:'#161b22',borderBottom:'1px solid rgba(0,0,0,0.12)'}}>
            <span style={{fontSize:10,color:'#8b949e',fontFamily:'var(--ff-mono)',textTransform:'uppercase',letterSpacing:1}}>{lang||'code'}</span>
            <button onClick={()=>navigator.clipboard.writeText(code)}
              style={{fontFamily:'inherit',fontSize:11,padding:'2px 8px',background:'var(--bg-elevated)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:5,cursor:'pointer',color:'#8b949e'}}>
              Copy
            </button>
          </div>
          <pre style={{margin:0,padding:'14px 16px',background:'var(--code-bg, #0d1117)',color:'var(--code-text, #e6edf3)',overflowX:'auto'}}>
            <code dangerouslySetInnerHTML={{__html:hl(code,lang)}}
              style={{fontFamily:'var(--ff-mono)',fontSize:13,lineHeight:1.65}} />
          </pre>
        </div>
      );
      i++; continue;
    }
    // Table
    if (line.startsWith('|')) {
      const tLines = [];
      while (i < lines.length && lines[i].startsWith('|')) { tLines.push(lines[i]); i++; }
      const rows = tLines.filter(l => !l.match(/^\|[\s|:-]+\|$/));
      if (rows.length > 1) {
        const parse = l => l.split('|').map(c=>c.trim()).filter(Boolean);
        const [hdr,...body] = rows;
        out.push(
          <div key={`t${i}`} style={{overflowX:'auto',margin:'10px 0'}}>
            <table style={{borderCollapse:'collapse',width:'100%',fontSize:13}}>
              <thead><tr>{parse(hdr).map((c,j)=><th key={j} style={{padding:'8px 14px',background:'rgba(201,151,58,0.1)',border:'1px solid rgba(201,151,58,0.2)',textAlign:'left',fontWeight:600,color:'var(--blue)',fontSize:11,textTransform:'uppercase',letterSpacing:0.8}}>{c}</th>)}</tr></thead>
              <tbody>{body.map((r,ri)=><tr key={ri}>{parse(r).map((c,j)=><td key={j} style={{padding:'8px 14px',border:'1px solid var(--border)',color:'var(--text2)',verticalAlign:'top'}}>{inl(c)}</td>)}</tr>)}</tbody>
            </table>
          </div>
        );
      } continue;
    }
    // Headings
    const h = line.match(/^(#{1,3})\s+(.+)/);
    if (h) {
      const lvl = h[1].length;
      const fs = lvl===1?18:lvl===2?16:14;
      const mt = lvl===1?20:16;
      out.push(<div key={i} style={{fontSize:fs,fontWeight:700,margin:`${mt}px 0 8px`,color:'var(--text)',fontFamily:'var(--ff-display)'}}>{inl(h[2])}</div>);
      i++; continue;
    }
    // HR
    if (line.match(/^(-{3,}|\*{3,})$/)) { out.push(<hr key={i} style={{border:'none',borderTop:'1px solid var(--border)',margin:'14px 0'}}/>); i++; continue; }
    // Blockquote
    if (line.startsWith('> ')) {
      const bq = [];
      while (i < lines.length && lines[i].startsWith('> ')) { bq.push(lines[i].slice(2)); i++; }
      out.push(<blockquote key={`bq${i}`} style={{borderLeft:'3px solid var(--gold-border)',padding:'4px 16px',margin:'10px 0',color:'var(--text2)',fontStyle:'italic',background:'rgba(201,151,58,0.04)',borderRadius:'0 6px 6px 0'}}>{bq.map((l,j)=><p key={j} style={{margin:'3px 0'}}>{inl(l)}</p>)}</blockquote>);
      continue;
    }
    // Lists
    if (line.match(/^[-*+]\s+/)) {
      const items = [];
      while (i < lines.length && lines[i].match(/^[-*+]\s+/)) { items.push(lines[i].replace(/^[-*+]\s+/,'')); i++; }
      out.push(<ul key={`ul${i}`} style={{paddingLeft:22,margin:'8px 0 10px'}}>{items.map((it,j)=><li key={j} style={{marginBottom:5,color:'var(--text)',lineHeight:1.65}}>{inl(it)}</li>)}</ul>);
      continue;
    }
    if (line.match(/^\d+\.\s+/)) {
      const items = [];
      while (i < lines.length && lines[i].match(/^\d+\.\s+/)) { items.push(lines[i].replace(/^\d+\.\s+/,'')); i++; }
      out.push(<ol key={`ol${i}`} style={{paddingLeft:24,margin:'8px 0 10px'}}>{items.map((it,j)=><li key={j} style={{marginBottom:5,color:'var(--text)',lineHeight:1.65}}>{inl(it)}</li>)}</ol>);
      continue;
    }
    if (!line.trim()) { out.push(<div key={`sp${i}`} style={{height:8}}/>); i++; continue; }
    out.push(<p key={i} style={{margin:'0 0 8px',lineHeight:1.75,color:'var(--text)'}}>{inl(line)}</p>);
    i++;
  }
  return <div style={{fontSize:14,wordBreak:'break-word'}}>{out}</div>;
}

/* ─── Code result card ─────────────────────────────────────────────────── */
function CodeResultCard({ artifact }) {
  return (
    <div className="artifact-card" style={{background:'var(--purple-soft)',border:'1px solid var(--purple-border)',padding:'12px 16px'}}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
        <span>⚡</span>
        <span style={{fontSize:12,fontWeight:600,color:'var(--purple)'}}>{artifact.title||'Calculation result'}</span>
      </div>
      <pre style={{margin:0,fontFamily:'var(--ff-mono)',fontSize:13,color:artifact.error?'var(--rose)':'var(--gold-2)',background:'rgba(0,0,0,0.2)',padding:'10px 14px',borderRadius:7,whiteSpace:'pre-wrap',lineHeight:1.6}}>
        {artifact.display}
      </pre>
    </div>
  );
}

/* ─── Search result card ───────────────────────────────────────────────── */
function SearchResultCard({ artifact }) {
  return (
    <div className="artifact-card" style={{background:'rgba(37,99,235,0.05)',border:'1px solid rgba(37,99,235,0.2)',padding:'12px 16px'}}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
        <span>🔍</span>
        <span style={{fontSize:12,fontWeight:600,color:'#7BA4F8'}}>Web Search: {artifact.query}</span>
      </div>
      {(artifact.results||[]).slice(0,3).map((r,i) => (
        <div key={i} style={{padding:'8px 0',borderBottom:i<2?'1px solid var(--border)':'none'}}>
          <a href={r.url} target="_blank" rel="noreferrer" style={{fontSize:13,fontWeight:500,color:'var(--blue)',textDecoration:'none',display:'block',marginBottom:2}}>{r.title}</a>
          <div style={{fontSize:12,color:'var(--text2)',lineHeight:1.5}}>{r.body}</div>
        </div>
      ))}
    </div>
  );
}


/* ─── Download button with expiry check ─────────────────────────────────── */
function DownloadBtn({ url, filename, col }) {
  const [expired, setExpired] = useState(false);
  const handleClick = async (e) => {
    if (expired) { e.preventDefault(); return; }
    // Check if file still exists before downloading
    try {
      const res = await fetch(url, { method: 'HEAD' });
      if (!res.ok) { e.preventDefault(); setExpired(true); }
    } catch { e.preventDefault(); setExpired(true); }
  };
  if (expired) {
    return (
      <span style={{fontSize:12,color:'var(--text3)',padding:'6px 12px',border:'1px solid var(--border)',borderRadius:7,display:'inline-flex',alignItems:'center',gap:5}}>
        ⏰ Expired
      </span>
    );
  }
  return (
    <a href={url} download={filename} onClick={handleClick}
      style={{display:'inline-flex',alignItems:'center',gap:6,fontFamily:'inherit',fontSize:12,fontWeight:600,padding:'6px 16px',background:`${col}0.15)`,border:`1px solid ${col}0.4)`,borderRadius:7,cursor:'pointer',color:'var(--sage-2)',textDecoration:'none',transition:'all 0.15s'}}
      onMouseEnter={e=>{e.currentTarget.style.background=`${col}0.25)`;}}
      onMouseLeave={e=>{e.currentTarget.style.background=`${col}0.15)`;}}
    >
      ⬇ Download
    </a>
  );
}

/* ─── Artifact card ─────────────────────────────────────────────────────── */
/* ─── DocUploadButton — proper component so useRef is a legal hook call ────── */
/* ─── IndustryDataCards — proper component for welcome screen data upload ──── */
function IndustryDataCards({ industry, companyName, showToast, sendMessage }) {
  const templates = INDUSTRY_DATA_TEMPLATES[industry] || [];
  const token = localStorage.getItem('token');

  const handleFile = async (file, t) => {
    const fd = new FormData();
    fd.append('file', file);
    showToast('Uploading and parsing file…', 'info');
    try {
      const res  = await fetch('/api/aistream/upload-context', {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      const prompt = t.prompt.replace(/{company}/g, companyName || 'this company');
      const msg = `I've uploaded our ${t.label} data (${file.name}).\n\nData preview:\n\`\`\`\n${data.content.slice(0,3000)}\n\`\`\`\n\n${prompt}`;
      showToast(`${file.name} ready — sending to AI`, 'success');
      sendMessage(msg);
    } catch(err) { showToast('Upload failed: ' + err.message, 'error'); }
  };

  if (!templates.length) return null;
  return (
    <div style={{marginBottom:28}}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
        <div style={{fontSize:10,fontWeight:700,letterSpacing:'0.1em',color:'var(--text-3)',textTransform:'uppercase'}}>
          Upload {industry} Data for AI Analysis
        </div>
        <span style={{fontSize:11,color:'var(--accent)',background:'var(--blue-soft)',border:'1px solid var(--blue-border)',borderRadius:4,padding:'1px 7px',fontWeight:600}}>
          New
        </span>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:10}}>
        {templates.map(t => (
          <label key={t.key} style={{
            cursor:'pointer', borderRadius:10, border:'1px solid var(--border)',
            padding:'12px 14px', background:'var(--bg-elevated)',
            transition:'border-color 0.15s, background 0.15s',
            display:'block',
          }}>
            <input type="file" accept=".csv,.xlsx,.xls,.txt,.pdf"
              style={{display:'none'}}
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) handleFile(f, t);
                e.target.value = '';
              }}
            />
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
              <span style={{fontSize:20}}>{t.icon}</span>
              <div style={{fontWeight:600,fontSize:12.5,color:'var(--text)'}}>{t.label}</div>
            </div>
            <div style={{fontSize:11,color:'var(--text-4)',lineHeight:1.4,marginBottom:8}}>
              Columns: <span style={{fontStyle:'italic'}}>{t.columns.slice(0,60)}</span>
            </div>
            <div style={{fontSize:11,fontWeight:600,color:'var(--accent)',display:'flex',alignItems:'center',gap:4}}>
              <span>↑</span> Upload CSV or Excel to analyse
            </div>
          </label>
        ))}
      </div>
      <div style={{marginTop:8,fontSize:11,color:'var(--text-4)'}}>
        Upload any CSV/Excel — AI will generate charts, models, and recommendations from your actual {industry} data.
      </div>
    </div>
  );
}

function DocUploadButton({ title, onUpload }) {
  const ref = React.useRef(null);
  return (
    <div>
      <input
        type="file"
        ref={ref}
        style={{ display: 'none' }}
        accept=".csv,.xlsx,.xls,.pdf,.txt,.docx,.md,.png,.jpg,.jpeg"
        onChange={e => {
          const f = e.target.files?.[0];
          if (f) onUpload(f, title);
          e.target.value = '';
        }}
      />
      <button
        onClick={() => ref.current?.click()}
        style={{
          width: '100%', padding: '9px', borderRadius: 8, cursor: 'pointer',
          border: '2px dashed var(--accent)', color: 'var(--accent)',
          background: 'var(--blue-soft)', fontWeight: 600, fontSize: 13,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}>
        <span style={{ fontSize: 16 }}>📎</span>
        Upload {title}
      </button>
    </div>
  );
}

/* ─── DataAnalysisBtn — proper component so useState is a legal hook call ──── */
function DataAnalysisBtn({ industry, companyName, showToast, sendMessage }) {
  const { useState } = React;
  const [open, setOpen] = useState(false);
  const templates = INDUSTRY_DATA_TEMPLATES[industry] || [];
  const token = localStorage.getItem('token');

  const handleUpload = async (file, template) => {
    setOpen(false);
    const fd = new FormData();
    fd.append('file', file);
    showToast('Uploading and parsing…', 'info');
    try {
      const res  = await fetch('/api/aistream/upload-context', {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      const prompt = template.prompt.replace(/{company}/g, companyName || 'this company');
      const msg = `I've uploaded our ${template.label} data (${file.name}).\n\nData:\n\`\`\`\n${data.content.slice(0,4000)}\n\`\`\`\n\n${prompt}`;
      showToast(`${file.name} — sending to AI`, 'success');
      sendMessage(msg);
    } catch(err) { showToast('Upload failed: ' + err.message, 'error'); }
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          fontSize: 11, padding: '4px 11px', borderRadius: 6, cursor: 'pointer',
          background: open ? 'var(--blue-soft)' : 'transparent',
          border: `1px solid ${open ? 'var(--accent)' : 'var(--border)'}`,
          color: open ? 'var(--accent)' : 'var(--text-3)',
          fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
        }}>
        📊 Analyse {industry} Data
      </button>

      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 98 }} onClick={() => setOpen(false)} />
          <div style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 99,
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 12, padding: 14, minWidth: 300, maxWidth: 360,
            boxShadow: '0 16px 48px rgba(0,0,0,0.3)',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Upload {industry} data for AI analysis
            </div>
            {templates.map(t => (
              <label key={t.key} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px',
                borderRadius: 8, cursor: 'pointer', marginBottom: 4,
                border: '1px solid var(--border)', background: 'var(--bg-elevated)',
              }}>
                <input type="file" accept=".csv,.xlsx,.xls,.txt,.pdf"
                  style={{ display: 'none' }}
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) handleUpload(f, t);
                    e.target.value = '';
                  }}
                />
                <span style={{ fontSize: 18, flexShrink: 0 }}>{t.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', marginBottom: 1 }}>{t.label}</div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-4)' }}>{t.columns.slice(0,45)}…</div>
                </div>
                <span style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600, flexShrink: 0 }}>↑</span>
              </label>
            ))}
            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-4)', lineHeight: 1.5 }}>
              Upload CSV or Excel • AI generates charts, models, and recommendations from your data
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ArtifactCard({ artifact, onExpand, onUploadContext, onFollowUp }) {
  const [open, setOpen] = useState(false);
  const [height, setHeight] = useState(400);
  const [qIndex, setQIndex] = useState(0);       // which clarification question is currently shown
  const [dismissed, setDismissed] = useState(false); // clarification widget answered/skipped/closed
  if (!artifact) return null;

  if (artifact.type === 'code_result') return <CodeResultCard artifact={artifact} />;
  if (artifact.type === 'search_result') return <SearchResultCard artifact={artifact} />;

  // ── Clarification Required card ───────────────────────────────────────────
  if (artifact.type === 'clarification') {
    const questions = artifact.questions || [];
    if (dismissed || questions.length === 0) return null;
    const total = questions.length;
    const q = questions[Math.min(qIndex, total - 1)];

    const goNext = () => {
      if (qIndex < total - 1) setQIndex(qIndex + 1);
      else setDismissed(true); // last question answered/skipped — widget done
    };
    const pickOption = (optionText) => {
      onFollowUp?.(optionText);
      goNext();
    };

    return (
      <div style={{border:'1px solid var(--border2)',background:'var(--white)',borderRadius:12,overflow:'hidden',marginTop:8,boxShadow:'var(--shadow-sm)'}}>
        <div style={{display:'flex',alignItems:'center',gap:10,padding:'14px 16px',borderBottom:'1px solid var(--border)'}}>
          <span style={{fontWeight:600,fontSize:14,color:'var(--text)',flex:1}}>{q.question}</span>
          {total > 1 && (
            <div style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:'var(--text-3)',flexShrink:0}}>
              <button onClick={()=>setQIndex(i=>Math.max(0,i-1))} disabled={qIndex===0}
                style={{background:'none',border:'none',cursor:qIndex===0?'default':'pointer',opacity:qIndex===0?0.3:1,fontSize:14,color:'var(--text-3)',padding:2}}>‹</button>
              <span>{qIndex+1} of {total}</span>
              <button onClick={()=>setQIndex(i=>Math.min(total-1,i+1))} disabled={qIndex===total-1}
                style={{background:'none',border:'none',cursor:qIndex===total-1?'default':'pointer',opacity:qIndex===total-1?0.3:1,fontSize:14,color:'var(--text-3)',padding:2}}>›</button>
            </div>
          )}
          <button onClick={()=>setDismissed(true)} title="Dismiss"
            style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-3)',fontSize:16,padding:2,flexShrink:0}}>✕</button>
        </div>
        <div>
          {(q.options || []).map((opt, i) => (
            <button key={i} onClick={()=>pickOption(opt)}
              style={{display:'flex',alignItems:'center',gap:12,width:'100%',textAlign:'left',padding:'12px 16px',background:'none',border:'none',borderBottom:'1px solid var(--border)',cursor:'pointer',fontFamily:'inherit',fontSize:13.5,color:'var(--text)',transition:'background 0.1s'}}
              onMouseEnter={e=>e.currentTarget.style.background='var(--bg-inset)'}
              onMouseLeave={e=>e.currentTarget.style.background='none'}>
              <span style={{color:'var(--text-4)',fontSize:12,flexShrink:0,width:16}}>{i+1}</span>
              <span>{opt}</span>
            </button>
          ))}
          <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 16px',background:'var(--bg-inset)'}}>
            <span style={{display:'flex',alignItems:'center',justifyContent:'center',width:26,height:26,borderRadius:6,background:'var(--border2)',fontSize:12,flexShrink:0}}>✎</span>
            <span style={{fontSize:13,color:'var(--text-3)',flex:1}}>Something else — just type your answer below</span>
            <button onClick={goNext}
              style={{fontFamily:'inherit',fontSize:12,padding:'5px 12px',background:'var(--white)',border:'1px solid var(--border2)',borderRadius:6,cursor:'pointer',color:'var(--text-3)',flexShrink:0}}>
              Skip
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Document Upload Request card ──────────────────────────────────────────
  if (artifact.type === 'document_request') {
    return (
      <div className="artifact-card" style={{ border: '1px solid var(--blue-border)', background: 'var(--blue-soft)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid var(--blue-border)' }}>
          <span style={{ fontSize: 18 }}>📎</span>
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--accent)' }}>Document Requested</div>
            <div style={{ fontSize: 11, color: 'var(--text-3)' }}>Enpower AI needs this to improve the output</div>
          </div>
        </div>
        <div style={{ padding: '12px 14px' }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>{artifact.title}</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginBottom: 10, lineHeight: 1.5 }}>{artifact.reason}</div>
          {(() => {
            // Defensive normalization: what_to_include SHOULD always be an
            // array per the tool schema, but that schema isn't strict-mode
            // enforced (unlike clarification_required/save_engagement_state),
            // so Claude can and did return a plain string here instead. A
            // string still passes `.length > 0` (strings have .length too!)
            // but then crashes on .map() — exactly what happened. Coerce
            // whatever shape arrives into a real array before rendering.
            const items = Array.isArray(artifact.what_to_include)
              ? artifact.what_to_include
              : (typeof artifact.what_to_include === 'string' && artifact.what_to_include.trim())
                ? [artifact.what_to_include]
                : [];
            if (items.length === 0) return null;
            return (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Include:</div>
                {items.map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, fontSize: 12, color: 'var(--text-2)', marginBottom: 3 }}>
                    <span style={{ color: 'var(--accent)', flexShrink: 0 }}>•</span>{item}
                  </div>
                ))}
              </div>
            );
          })()}
          {artifact.format_hint && (
            <div style={{ fontSize: 11, color: 'var(--text-4)', marginBottom: 12 }}>
              Accepted: {artifact.format_hint}
            </div>
          )}
          {/* Upload button — calls the context upload endpoint */}
          {onUploadContext && (
            <DocUploadButton title={artifact.title} onUpload={onUploadContext} />
          )}
        </div>
      </div>
    );
  }

  // ── Saved Insight card ────────────────────────────────────────────────────
  if (artifact.type === 'save_result') {
    const catColors = { risk:'var(--red)', opportunity:'var(--green)', pattern:'var(--blue)', recommendation:'var(--accent)', benchmark:'var(--gold)' };
    return (
      <div className="artifact-card" style={{ border: `1px solid ${catColors[artifact.category]||'var(--border)'}20`, padding: '10px 14px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <span style={{ fontSize: 16, flexShrink: 0 }}>💾</span>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: catColors[artifact.category]||'var(--accent)', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom: 3 }}>
            {artifact.category} saved
          </div>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 3 }}>{artifact.title}</div>
          <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{artifact.body}</div>
        </div>
      </div>
    );
  }

  if (artifact.type === 'html_artifact') {
    return (
      <div className="artifact-card" style={{border:'1px solid rgba(201,151,58,0.25)'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'9px 14px',background:'rgba(201,151,58,0.06)',borderBottom:'1px solid rgba(201,151,58,0.15)'}}>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <span style={{fontSize:15}}>{artifact.icon||'🖥️'}</span>
            <span style={{fontSize:13,fontWeight:600,color:'var(--blue)'}}>{artifact.title}</span>
            {artifact.description && <span style={{fontSize:11,color:'var(--text3)'}}>— {artifact.description}</span>}
          </div>
          <div style={{display:'flex',gap:6}}>
            <button onClick={()=>setOpen(o=>!o)}
              style={{fontFamily:'inherit',fontSize:11,padding:'4px 12px',background:open?'var(--indigo-s)':'var(--b0)',border:`1px solid ${open?'var(--indigo-b)':'var(--b2)'}`,borderRadius:6,cursor:'pointer',color:open?'var(--gold-2)':'#8b949e',transition:'all 0.15s'}}>
              {open ? '▼ Collapse' : '▶ Show'}
            </button>
            <button onClick={() => onExpand?.(artifact.html)}
              style={{fontFamily:'inherit',fontSize:11,padding:'4px 10px',background:'var(--white)',border:'1px solid var(--border2)',borderRadius:6,cursor:'pointer',color:'var(--text2)'}}>
              ⊞
            </button>
            {artifact.downloadUrl && (
              <a href={artifact.downloadUrl} download={artifact.filename}
                style={{fontFamily:'inherit',fontSize:11,padding:'4px 10px',background:'var(--white)',border:'1px solid var(--border2)',borderRadius:6,cursor:'pointer',color:'var(--text2)',textDecoration:'none',display:'inline-flex',alignItems:'center',gap:4}}
                title="Download as standalone .html file">
                ⬇ Download
              </a>
            )}
          </div>
        </div>
        {open && <iframe srcDoc={artifact.html} className="html-artifact-frame" style={{height}} sandbox="allow-scripts allow-same-origin allow-downloads" title={artifact.title}
          onLoad={e => { try { const h = e.target.contentDocument?.documentElement?.scrollHeight; if (h > 100) setHeight(Math.min(h+20, 600)); } catch {} }} />}
      </div>
    );
  }

  if (artifact.type === 'file' || artifact.type === 'code') {
    const dlUrl = artifact.downloadUrl ? `/api/tool-output/${artifact.downloadUrl.split('/').pop()}` : null;
    const typeColors = { excel:'rgba(46,125,92,', pptx:'rgba(192,57,77,', word:'rgba(37,99,235,', code:'rgba(124,58,237,' };
    const col = typeColors[artifact.fileType] || 'rgba(201,151,58,';
    return (
      <div className="artifact-card file-artifact" style={{background:`${col}0.07)`,border:`1px solid ${col}0.25)`}}>
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          <div style={{width:40,height:40,borderRadius:10,background:`${col}0.15)`,border:`1px solid ${col}0.3)`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:20,flexShrink:0}}>
            {artifact.icon||'📎'}
          </div>
          <div>
            <div style={{fontWeight:600,fontSize:13,color:'var(--text)',marginBottom:2}}>{artifact.filename}</div>
            <div style={{fontSize:11,color:'var(--text3)'}}>{artifact.description}</div>
          </div>
        </div>
        <div style={{display:'flex',gap:8,flexShrink:0,flexWrap:'wrap'}}>
          {artifact.code && (
            <button onClick={()=>setOpen(o=>!o)}
              style={{fontFamily:'inherit',fontSize:12,padding:'6px 12px',background:'var(--paper-2)',border:'1px solid var(--border2)',borderRadius:7,cursor:'pointer',color:'var(--text2)'}}>
              {open?'Hide':'View code'}
            </button>
          )}
          {dlUrl && (
            <DownloadBtn url={dlUrl} filename={artifact.filename} col={col} />
          )}
        </div>
        {open && artifact.code && (
          <div style={{width:'100%',marginTop:8}}>
            <pre style={{background:'var(--code-bg, #0d1117)',borderRadius:8,padding:'12px 16px',fontSize:12,fontFamily:'var(--ff-mono)',overflowX:'auto',color:'var(--code-text, #e6edf3)',lineHeight:1.6,border:'1px solid rgba(0,0,0,0.2)',whiteSpace:'pre-wrap',margin:0}}>{artifact.code}</pre>
          </div>
        )}
      </div>
    );
  }
  return null;
}

/* ─── Tool status bar — the key UX element ─────────────────────────────── */
function ToolStatusBar({ status, thinkingDone, steps, isStreaming, liveStep }) {
  const [expanded, setExpanded] = React.useState(false);
  const hasSteps = (steps && steps.length > 0) || !!liveStep;
  const logRef = React.useRef(null);

  // Auto-scroll the expanded log to the bottom as live text streams in
  React.useEffect(() => {
    if (expanded && logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [expanded, liveStep, steps?.length]);

  return (
    <div style={{marginBottom:8}}>
      {/* Active working indicator — collapsible */}
      {isStreaming && (status || hasSteps) && (
        <div style={{display:'inline-flex',alignItems:'center',gap:6,cursor: hasSteps ? 'pointer' : 'default',
          padding:'4px 10px',background:'rgba(37,99,235,0.07)',border:'1px solid rgba(37,99,235,0.2)',
          borderRadius:20,fontSize:12,color:'var(--blue)',marginBottom:4,userSelect:'none'}}
          onClick={() => hasSteps && setExpanded(e => !e)}>
          <div style={{display:'flex',gap:2}}>
            <span className="thinking-dot"/><span className="thinking-dot"/><span className="thinking-dot"/>
          </div>
          <span style={{fontWeight:600}}>{status || 'Working…'}</span>
          {hasSteps && (
            <span style={{fontSize:10,opacity:0.7,marginLeft:2}}>
              {expanded ? '▲ hide' : '▼ ' + (steps?.length||0) + ' step' + ((steps?.length||0)!==1?'s':'') + (liveStep ? ' · live' : '')}
            </span>
          )}
        </div>
      )}

      {/* Expanded steps log — includes a live-streaming line while Claude is
          mid-narration, so opening this while generating shows real-time text
          instead of a static block appearing after the fact. */}
      {expanded && hasSteps && (
        <div ref={logRef} style={{marginTop:4,marginLeft:4,padding:'8px 12px',
          background:'var(--bg-inset)',border:'1px solid var(--border)',borderRadius:8,fontSize:11,
          fontFamily:'var(--ff-mono)',color:'var(--text-3)',lineHeight:1.8,maxHeight:220,overflowY:'auto'}}>
          {(steps||[]).map((s, i) => (
            <div key={i} style={{display:'flex',alignItems:'flex-start',gap:8}}>
              <span style={{color:'var(--text-4)',flexShrink:0}}>
                {new Date(s.ts).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit',second:'2-digit'})}
              </span>
              <span>{s.text}</span>
            </div>
          ))}
          {liveStep && (
            <div style={{display:'flex',alignItems:'flex-start',gap:8,marginTop:6,paddingTop:6,borderTop:'1px dashed var(--border)'}}>
              <span style={{color:'var(--blue)',flexShrink:0}}>now</span>
              <span style={{color:'var(--text-2)',whiteSpace:'pre-wrap'}}>
                {liveStep}
                <span style={{display:'inline-block',width:6,height:11,background:'var(--blue)',marginLeft:2,animation:'cursor-blink 1s step-end infinite',verticalAlign:'text-bottom'}}/>
              </span>
            </div>
          )}
        </div>
      )}

      {/* Thinking done badge */}
      {thinkingDone && !status && (
        <div style={{display:'inline-flex',alignItems:'center',gap:6,padding:'3px 10px',
          background:'rgba(124,58,237,0.08)',border:'1px solid var(--purple-border)',
          borderRadius:20,fontSize:11,color:'var(--purple)',marginBottom:6}}>
          🧠 {thinkingDone}
        </div>
      )}
    </div>
  );
}


/* ─── Follow-up suggestion chips — parsed from "Next steps:" section ──────── */
function FollowUps({ content, onPick, disabled }) {
  if (!content) return null;

  // Detect truncation — show a "Continue" chip prominently
  const wasTruncated = content.includes('[Response was truncated') || content.includes('⚠️');
  if (wasTruncated) {
    // Strip the truncation notice from what we send back — Claude doesn't need to see it
    const cleanContent = content
      .replace(/\*?\[Response was truncated[^\]]*\]\*?/gi, '')
      .replace(/⚠️[^\n]*/g, '')
      .trim();
    // Send the partial response back as context so Claude knows exactly what it already said
    const continuePrompt = `You were in the middle of a response and it was cut off. Here is what you had written so far:\n\n---\n${cleanContent.slice(-3000)}\n---\n\nPlease continue directly from where you left off. Do not repeat what you have already written. Do not re-introduce yourself or restate the context. Just continue the response naturally from the cut-off point.`;
    const summarisePrompt = `You were in the middle of a response that got cut off. Here is what you had written:\n\n---\n${cleanContent.slice(-2000)}\n---\n\nPlease: 1) Briefly confirm what you covered above, 2) List what still needs to be covered to complete the original request, 3) Then complete those remaining parts.`;
    return (
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:10 }}>
        <button className="followup-chip" disabled={disabled}
          style={{ borderColor:'var(--gold-border)', color:'var(--gold)', background:'rgba(201,151,58,0.08)' }}
          onClick={() => onPick(continuePrompt)}>
          ▶ Continue response
        </button>
        <button className="followup-chip" disabled={disabled}
          onClick={() => onPick(summarisePrompt)}>
          📋 What's left to cover?
        </button>
      </div>
    );
  }

  const m = content.match(/\*\*Next steps:?\*\*([\s\S]*?)$/i);
  if (!m) return null;
  const items = m[1].split('\n')
    .map(l => l.replace(/^[-*\d.\s"]+/, '').replace(/["]+$/, '').trim())
    .filter(l => l.length > 8 && l.length < 140)
    .slice(0, 3);
  if (!items.length) return null;
  return (
    <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:10 }}>
      {items.map((t, i) => (
        <button key={i} className="followup-chip" disabled={disabled} onClick={() => onPick(t)}>
          ✦ {t.length > 64 ? t.slice(0, 62) + '…' : t}
        </button>
      ))}
    </div>
  );
}

/* ─── Message bubble ────────────────────────────────────────────────────── */
function MsgBubble({ msg, isLast, onCopy, onRegenerate, onPreview, isStreaming, onFollowUp, onUploadContext }) {
  if (msg.role === 'user') {
    const text = typeof msg.content==='string' ? msg.content : msg.content?.find?.(c=>c.type==='text')?.text||'';
    const imgs = Array.isArray(msg.content) ? msg.content.filter(c=>c.type==='image') : [];
    const docs = Array.isArray(msg.content) ? msg.content.filter(c=>c.type==='document') : [];
    return (
      <div style={{display:'flex',justifyContent:'flex-end',marginBottom:20}}>
        <div style={{maxWidth:'75%'}}>
          {imgs.map((img,i)=>(
            <div key={i} style={{marginBottom:6,borderRadius:10,overflow:'hidden',border:'1px solid var(--border)'}}>
              <img src={`data:${img.source?.media_type};base64,${img.source?.data}`} alt="upload" style={{maxWidth:'100%',display:'block'}}/>
            </div>
          ))}
          {docs.map((d,i)=>(
            <div key={i} style={{marginBottom:6,padding:'6px 12px',background:'var(--gold-soft)',borderRadius:8,fontSize:12,color:'var(--blue)',display:'flex',alignItems:'center',gap:6,border:'1px solid var(--gold-border)'}}>📄 {d.name||'Document'}</div>
          ))}
          {text && (
            <div style={{padding:'11px 16px',background:'var(--indigo-s)',border:'1px solid var(--indigo-b)',borderRadius:'16px 16px 4px 16px',fontSize:14,color:'var(--text)',lineHeight:1.65,whiteSpace:'pre-wrap'}}>
              {text}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{marginBottom:24}}>
      <div style={{display:'flex',gap:12,alignItems:'flex-start'}}>
        {/* Animated orb */}
        <div className={`chat-orb ${msg.streaming ? (msg.toolStatus?.includes('Thinking')||msg.toolStatus?.includes('🧠') ? 'thinking' : 'streaming') : 'idle'}`}
          style={{marginTop:2}}>
          {!msg.streaming && <span style={{fontSize:12}}>✦</span>}
          {msg.streaming && !msg.toolStatus && (
            <div style={{display:'flex',gap:3}}>
              <span className="thinking-dot" style={{color:'var(--blue)'}}/>
              <span className="thinking-dot" style={{color:'var(--blue)'}}/>
              <span className="thinking-dot" style={{color:'var(--blue)'}}/>
            </div>
          )}
          {msg.streaming && msg.toolStatus && <span style={{fontSize:10,color:'var(--blue)'}}>⚙</span>}
        </div>

        <div style={{flex:1,minWidth:0}}>
          <ToolStatusBar status={msg.toolStatus} thinkingDone={msg.thinkingDone} steps={msg.steps} isStreaming={msg.streaming} liveStep={msg.liveStep}/>

          {/* Text content */}
          {msg.streaming && !msg.content && !msg.toolStatus ? (
            <div style={{display:'flex',gap:4,padding:'6px 0',alignItems:'center'}}>
              <span className="thinking-dot" style={{color:'var(--text3)'}}/>
              <span className="thinking-dot" style={{color:'var(--text3)'}}/>
              <span className="thinking-dot" style={{color:'var(--text3)'}}/>
            </div>
          ) : msg.streaming ? (
            <div style={{fontSize:14,lineHeight:1.75,color:'var(--text)'}}>
              {msg.content}
              <span style={{display:'inline-block',width:2,height:15,background:'var(--gold)',animation:'cursor-blink 1s step-end infinite',verticalAlign:'text-bottom',marginLeft:1,borderRadius:1}}/>
            </div>
          ) : (
            <Markdown content={msg.content} onPreview={onPreview}/>
          )}

          {/* Artifacts */}
          {(msg.artifacts||[]).map((a,i) => (
            <ArtifactCard key={i} artifact={a} onExpand={html => onPreview?.(html,'html')} onUploadContext={onUploadContext} onFollowUp={onFollowUp}/>
          ))}

          {/* Actions */}
          {!msg.streaming && msg.content && isLast && (
            <FollowUps content={msg.content} disabled={isStreaming} onPick={t => onFollowUp?.(t)} />
          )}
          {!msg.streaming && msg.content && (
            <div style={{display:'flex',gap:4,marginTop:8}}>
              <button className="msg-act-btn" onClick={()=>onCopy(msg.content)}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                Copy
              </button>
              {isLast && (
                <button className="msg-act-btn" onClick={onRegenerate}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M23 4v6h-6"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/></svg>
                  Regenerate
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Full-screen artifact panel ─────────────────────────────────────────── */
function ArtifactPanel({ html, onClose }) {
  return (
    <div style={{
      position:'fixed', inset:0, zIndex:200,
      display:'flex', flexDirection:'column',
      background:'var(--bg-surface)',
    }}>
      <div style={{padding:'10px 16px',borderBottom:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center',flexShrink:0}}>
        <span style={{fontSize:13,color:'var(--blue)',fontWeight:600}}>Preview</span>
        <button onClick={onClose}
          style={{fontFamily:'inherit',fontSize:12,padding:'4px 10px',background:'none',border:'1px solid var(--border2)',borderRadius:6,cursor:'pointer',color:'var(--text3)'}}>
          ✕ Close
        </button>
      </div>
      <iframe srcDoc={html} style={{flex:1,border:'none',background:'#fff'}} sandbox="allow-scripts allow-same-origin allow-downloads" title="Preview"/>
    </div>
  );
}

/* ─── Attach chip ─────────────────────────────────────────────────────────── */
function AttachChip({ file, onRemove }) {
  return (
    <div style={{display:'inline-flex',alignItems:'center',gap:6,padding:'4px 10px 4px 7px',background:'var(--gold-soft)',border:'1px solid var(--gold-border)',borderRadius:20,fontSize:12,color:'var(--text2)'}}>
      {file.preview ? <img src={file.preview} alt="" style={{width:20,height:20,borderRadius:3,objectFit:'cover'}}/> : <span>📄</span>}
      <span style={{maxWidth:130,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{file.name}</span>
      <button onClick={onRemove} style={{background:'none',border:'none',cursor:'pointer',color:'var(--text3)',fontSize:14,lineHeight:1,padding:0}}>×</button>
    </div>
  );
}

/* ─── Industry-specific data analysis templates ──────────────────────────── */
const INDUSTRY_DATA_TEMPLATES = {
  "Technology": [
    { key:"sales_crm",      icon:"💹", label:"CRM / Pipeline Data",   columns:"Company, Stage, Value (₹), Expected Close, Owner",
      prompt:`Analyse the sales pipeline data I just uploaded. Create: 1) Pipeline by stage funnel chart, 2) Revenue forecast Excel (by month, by rep, by product), 3) Win-rate analysis by stage and segment, 4) 3 specific recommendations to improve conversion based on the data.` },
    { key:"sprint_metrics", icon:"🏃", label:"Sprint / Dev Metrics",   columns:"Sprint, Story Points Completed, Bugs, Velocity, Team",
      prompt:`Analyse the engineering/sprint metrics data. Create: 1) Velocity trend line chart, 2) Bug rate vs velocity correlation chart, 3) Team capacity model Excel (with sprint planning template), 4) Top 3 bottlenecks identified from the data with fixes.` },
    { key:"hr_attrition",   icon:"👥", label:"Headcount & Attrition",  columns:"Name, Department, Joining Date, Exit Date (if any), Salary Band",
      prompt:`Analyse the HR headcount and attrition data. Create: 1) Attrition by department bar chart, 2) Tenure distribution histogram, 3) Attrition risk scorecard Excel, 4) Retention strategy with specific interventions based on the patterns in this data.` },
  ],
  "Manufacturing": [
    { key:"oee_data",       icon:"🏭", label:"OEE / Production Data",  columns:"Date, Machine, Availability %, Performance %, Quality %, OEE%",
      prompt:`Analyse the OEE production data. Create: 1) OEE trend by machine line chart, 2) Loss waterfall chart (Availability vs Performance vs Quality losses), 3) Downtime tracker Excel with Pareto of causes, 4) Prioritised improvement plan with ROI estimate per initiative.` },
    { key:"quality_data",   icon:"✅", label:"Quality / Defect Data",  columns:"Date, Product, Defect Type, Quantity, Production Line",
      prompt:`Analyse the quality defect data. Create: 1) Pareto chart of top defect types (80/20 rule), 2) Defect trend by product line, 3) SPC-style control chart for top defect, 4) Root cause analysis document for the top 3 defects with corrective actions.` },
    { key:"inventory_data", icon:"📦", label:"Inventory / Stock Data", columns:"SKU, Description, Opening Stock, Closing Stock, Reorder Level, Lead Time",
      prompt:`Analyse the inventory data. Create: 1) Stockout risk dashboard (items below reorder level), 2) Slow/dead stock analysis chart, 3) EOQ calculator Excel (Economic Order Quantity per SKU), 4) Inventory rationalisation recommendations with cash release potential (₹).` },
  ],
  "Retail": [
    { key:"sales_by_sku",   icon:"🛒", label:"Sales by SKU",           columns:"Date, SKU, Product Name, Qty Sold, Revenue (₹), Category, Store",
      prompt:`Analyse the SKU-level sales data. Create: 1) Revenue by category Pareto chart, 2) Top 20 / Bottom 20 SKU performance table, 3) Seasonal trend analysis (by week/month), 4) SKU rationalisation recommendations — which to push, which to delist, with revenue impact.` },
    { key:"footfall_data",  icon:"👣", label:"Footfall & Conversion",  columns:"Date, Store, Footfall, Transactions, Conversion %, Avg Basket Size (₹)",
      prompt:`Analyse footfall and conversion data. Create: 1) Store conversion performance comparison chart, 2) Peak hours/days heatmap HTML app, 3) Basket size trend analysis, 4) 5 specific interventions to lift conversion rate, quantified by potential revenue impact.` },
    { key:"customer_data",  icon:"⭐", label:"Customer / NPS Data",     columns:"Customer ID, Purchase Frequency, Total Spend (₹), NPS Score, Last Purchase Date",
      prompt:`Analyse customer value and NPS data. Create: 1) NPS trend chart, 2) RFM segmentation Excel (Recency, Frequency, Monetary), 3) Customer lifetime value model, 4) Personalised retention strategy for each RFM segment.` },
  ],
  "Financial Services": [
    { key:"portfolio_data", icon:"📊", label:"Portfolio / AUM Data",   columns:"Client, Product, AUM (₹), 1Y Returns %, Risk Profile, Client Age",
      prompt:`Analyse the portfolio/AUM data. Create: 1) AUM breakdown by product and risk profile, 2) Returns vs benchmark comparison chart, 3) Client concentration risk analysis (top 10 clients as % of AUM), 4) Portfolio rebalancing recommendations with rationale.` },
    { key:"collections",    icon:"💰", label:"Collections / NPA Data", columns:"Borrower, Loan Amount (₹), EMI, Days Past Due, NPA Stage, Branch",
      prompt:`Analyse the collections and NPA data. Create: 1) Ageing bucket analysis chart (0-30, 31-60, 61-90, 90+ DPD), 2) NPA by branch/product heat analysis, 3) Collection efficiency scorecard Excel, 4) Early warning indicators dashboard with trigger rules.` },
  ],
  "Healthcare": [
    { key:"opd_data",       icon:"🏥", label:"OPD / Patient Volumes",  columns:"Date, Department, Patients, Revenue (₹), Avg Wait Time (min), Doctors On Duty",
      prompt:`Analyse OPD operational data. Create: 1) Department load comparison chart, 2) Revenue per patient trend, 3) Wait time vs patient volume correlation, 4) Capacity planning model Excel (beds/doctors needed based on demand patterns).` },
    { key:"billing_data",   icon:"💊", label:"Billing / Revenue Cycle", columns:"Patient ID, Department, Procedure, Gross Billing (₹), Collected, Insurance Type, Days to Collect",
      prompt:`Analyse the revenue cycle / billing data. Create: 1) Collection efficiency by department chart, 2) Insurance vs self-pay vs corporate payer mix, 3) Days-to-collect distribution analysis, 4) Revenue cycle improvement roadmap with specific bottleneck fixes.` },
  ],
  "Education": [
    { key:"enrollment",     icon:"🎓", label:"Enrollment & Fee Data",  columns:"Student ID, Grade/Course, Enrollment Date, Fee Amount (₹), Amount Paid, Outstanding, Payment Date",
      prompt:`Analyse enrollment and fee collection data. Create: 1) Enrollment trend chart (MoM), 2) Fee collection efficiency by grade/programme, 3) Overdue/defaulter tracking Excel with risk classification, 4) Revenue forecast model for next 3 terms.` },
    { key:"academic",       icon:"📝", label:"Academic Performance",   columns:"Student ID, Subject, Marks, Grade, Class, Teacher",
      prompt:`Analyse academic performance data. Create: 1) Subject-wise performance distribution chart, 2) At-risk student identifier (bottom 20% per subject), 3) Teacher effectiveness comparison, 4) Intervention programme document for struggling students.` },
  ],
  "Professional Services": [
    { key:"utilisation",    icon:"⏱",  label:"Utilisation & Billing",  columns:"Consultant, Client, Project, Billed Hours, Total Hours, Bill Rate (₹/hr), Month",
      prompt:`Analyse consultant utilisation and billing data. Create: 1) Utilisation rate by consultant chart (target 75-80%), 2) Realisation rate analysis (billed vs available), 3) Client profitability model Excel (revenue vs cost per client), 4) Capacity planning and pyramid recommendations.` },
    { key:"pipeline_ps",    icon:"🎯", label:"Client Pipeline",         columns:"Prospect, Service Line, Value (₹), Stage, Probability %, Owner, Expected Close",
      prompt:`Analyse the business development pipeline. Create: 1) Weighted pipeline by stage and service line, 2) Win rate analysis by service and partner, 3) 12-month revenue forecast model, 4) Business development focus recommendations based on pipeline data.` },
  ],
  "Real Estate": [
    { key:"project_sales",  icon:"🏗",  label:"Project Sales Data",     columns:"Project, Unit No, Type, Area (sqft), Listed Price (₹), Sale Price (₹), Booking Date, Buyer",
      prompt:`Analyse real estate project sales data. Create: 1) Sales velocity by project (units/month), 2) Price realisation analysis (listed vs sold), 3) Inventory movement dashboard HTML app, 4) Pricing strategy recommendations with competitive positioning.` },
    { key:"collections_re", icon:"💰", label:"Collections & Receivables",columns:"Customer, Project, Unit, Total Value (₹), Amount Received, Outstanding, Due Date",
      prompt:`Analyse project receivables data. Create: 1) Collection efficiency chart by project, 2) Overdue ageing analysis (0-30, 31-90, 90+ days), 3) Cash flow forecast Excel (12 months), 4) Collections escalation strategy and incentive structure.` },
  ],
  "FMCG": [
    { key:"gt_distribution","icon":"🚚", label:"GT / Distributor Data", columns:"Distributor, Territory, Month, Sales (₹), Target (₹), Active Outlets, Total Outlets",
      prompt:`Analyse general trade distribution data. Create: 1) Territory performance vs target comparison, 2) Outlet coverage heatmap, 3) Distributor health scorecard Excel, 4) Focus territory and programme recommendations with volume uplift potential.` },
    { key:"brand_perf",     icon:"📊", label:"Brand / SKU Performance", columns:"Brand, SKU, Region, Month, Sales Volume (units), Value (₹), Market Share %",
      prompt:`Analyse brand and SKU performance. Create: 1) Brand revenue mix Pareto chart, 2) Regional performance comparison, 3) Market share trend analysis, 4) Portfolio strategy — push/protect/prune recommendations with rationale.` },
  ],
};

/* ─── Playbooks ───────────────────────────────────────────────────────────── */
const PLAYBOOKS = [
  {
    id: 'marketing_campaign',
    icon: '📢',
    title: 'Marketing Campaign',
    desc: 'Strategy, content calendar, budget model, execution tracker',
    color: '#f43f5e',
    prompt: `Design a complete marketing campaign for {company}. Create all 4 artifacts now in sequence:
1. Campaign strategy document (positioning, target segments, key messages, channels, KPIs)
2. 90-day content calendar in Excel (week-by-week, channel, content type, owner, status)
3. Campaign budget model in Excel (channel allocation, projected reach, CPC/CPL estimates, ROI projection)
4. Campaign execution checklist document (launch sequence, approval gates, measurement cadence)
Make all artifacts specific to {industry} and use real Indian market benchmarks where applicable.`
  },
  {
    id: 'sales_process',
    icon: '📈',
    title: 'Sales Process Design',
    desc: 'Pipeline stages, qualification framework, CRM setup, team structure',
    color: '#22c55e',
    prompt: `Design a complete B2B sales process for {company} in {industry}. Create:
1. Sales process flowchart (stages from lead to close with decision gates)
2. Lead qualification BANT/MEDDIC framework in Excel (scoring criteria, weightings)
3. Sales playbook document (ICP definition, discovery questions, objection handling, pitch framework)
4. Sales KPI dashboard HTML app (pipeline metrics, conversion rates, rep performance)
Use Indian SME sales context and ₹ currency throughout.`
  },
  {
    id: 'hr_system',
    icon: '👥',
    title: 'HR & People System',
    desc: 'Onboarding, performance management, career ladder, retention',
    color: '#ec4899',
    prompt: `Design a complete HR system for {company} ({size} people, {industry}). Create:
1. Employee onboarding checklist (30-60-90 day plan in Excel with owner columns)
2. Performance review framework document (OKR structure, rating rubric, calibration process)
3. Career ladder for 3 functions (IC track and management track, level definitions)
4. Retention risk dashboard HTML app (key risk indicators, early warning signals)
Tailor to Indian labour law context (PF, gratuity, leave policies).`
  },
  {
    id: 'risk_assessment',
    icon: '⚠️',
    title: 'Risk Assessment',
    desc: 'Risk register, mitigation plans, compliance checklist, monitoring dashboard',
    color: '#f97316',
    prompt: `Conduct a comprehensive risk assessment for {company} in {industry}. Create:
1. Risk register in Excel (risk category, description, likelihood 1-5, impact 1-5, risk score, owner, mitigation, status)
2. Top 5 risks with detailed mitigation plans (document format, 3 actions each)
3. Compliance checklist relevant to {industry} in India (GST, ROC, labour law, data privacy, sector-specific)
4. Risk monitoring dashboard HTML app (colour-coded risk matrix, monthly review tracker)
Be specific about Indian regulatory requirements.`
  },
  {
    id: 'financial_model',
    icon: '💰',
    title: 'Financial Model',
    desc: 'P&L projection, unit economics, scenario planning, investor dashboard',
    color: '#10b981',
    prompt: `Build a complete financial model for {company} in {industry}. Create:
1. 3-year P&L projection in Excel (monthly for Year 1, quarterly for Y2-Y3, with assumptions tab)
2. Unit economics model (CAC, LTV, payback period, contribution margin — specific to {industry})
3. 3-scenario model (Base, Bull, Bear) with key variable toggles
4. Executive financial dashboard HTML app (KPIs, charts, traffic light system)
Use ₹ (Indian Rupees) and Indian tax structure (GST 18%, corporate tax 25.17%).`
  },
  {
    id: 'customer_experience',
    icon: '⭐',
    title: 'Customer Experience',
    desc: 'Journey map, NPS program, service standards, escalation framework',
    color: '#8b5cf6',
    prompt: `Design a complete customer experience system for {company} in {industry}. Create:
1. Customer journey map (all touchpoints from awareness to advocacy, emotion curve, pain points)
2. NPS/CSAT measurement program (survey questions, frequency, calculation method, action triggers)
3. Service standards document (response SLAs, quality benchmarks, scripts for key interactions)
4. Customer escalation flowchart (Level 1-2-3 escalation with ownership and resolution times)
Make specific to Indian customer expectations and context.`
  },
];

/* ─── Suggestions ────────────────────────────────────────────────────────── */
const SUGGESTIONS = [
  { icon:'📊', text:'Build a KPI dashboard with our actual scores' },
  { icon:'🎯', text:'What should we fix first based on our data?' },
  { icon:'📋', text:'Create a complete RACI matrix for our org' },
  { icon:'🔀', text:'Design our decision-making process as a flowchart' },
  { icon:'📑', text:'Build a 10-slide board deck on our diagnostic' },
  { icon:'🧠', text:'What will break if we try to 2x in 18 months?' },
  { icon:'📄', text:'Write a Decision-Making SOP for our team' },
  { icon:'📈', text:'Show all 12 dimension scores as a radar chart' },
];

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */
export default function AIWorkspace({ showToast }) {
  const navigate = useNavigate();
  const location = useLocation();
  const pageData = useMemo(() => location.state || {}, [location.state]);
  const { user }                                  = useAuth();
  const [companies, setCompanies]                 = useState([]);
  const [selCo, setSelCo]                         = useState('');
  const [orgStructure, setOrgStructure]           = useState(null); // {name, text, uploading}
  const [showOrgPrompt, setShowOrgPrompt]         = useState(false);
  const [messages, setMessages]                   = useState([]);
  // Kept in sync via useEffect below — provides SYNCHRONOUS access to the
  // latest messages state. This is necessary because `setMessages(prev => {
  // capture = prev; ... })` does NOT guarantee the updater runs before the
  // next line of code executes — React can defer it, meaning code relying on
  // that capture immediately after the setState call sees a stale (often
  // empty) value. This was the actual root cause of "AI forgets everything" —
  // sendMessage's history-building was always operating on an empty array.
  const messagesRef = useRef([]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  const [input, setInput]                         = useState('');
  const [attachments, setAttachments]             = useState([]);
  // Same fix as messagesRef above, same underlying bug — synchronous access
  // to the latest attachments state.
  const attachmentsRef = useRef([]);
  useEffect(() => { attachmentsRef.current = attachments; }, [attachments]);
  const [streaming, setStreaming]                 = useState(false);
  const [currentTool, setCurrentTool]             = useState(null); // tool being executed right now
  const [artifactPanel, setArtifactPanel]         = useState(null); // full-screen preview html
  const [histLoading, setHistLoading]             = useState(false);
  const [histLoaded, setHistLoaded]               = useState(false); // true only AFTER history fetch completes
  const [apiMissing, setApiMissing]               = useState(false);
  const [showBuiltBanner, setShowBuiltBanner]     = useState(false);
  const [tokenCount, setTokenCount]               = useState(null);
  const [directMode, setDirectMode]               = useState(false); // true = user knows problem, skip audit framing

  const endRef         = useRef();
  const fileRef        = useRef();
  const textareaRef    = useRef(null);
  const abortRef       = useRef(null);
  const streamingRef   = useRef(false);
  const autoFiredRef   = useRef('');
  const tokenTimer     = useRef(null);
  const sendMsgRef     = useRef(null); // always points to latest sendMessage - avoids stale closure
  const pageDataRef    = useRef(pageData);
  useEffect(() => { pageDataRef.current = pageData; }, [pageData]);

  // Inject CSS once
  useEffect(() => {
    const id = 'enpower-ai-workspace-css';
    if (!document.getElementById(id)) {
      const s = document.createElement('style'); s.id = id; s.textContent = CHAT_CSS;
      document.head.appendChild(s);
    }
  }, []);

  // Load companies
  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch('/api/companies', { headers:{ Authorization:`Bearer ${token}` }})
      .then(r=>r.json()).then(d=>setCompanies(Array.isArray(d)?d:[])).catch(()=>{});
  }, []);

  // Set company from pageData. If we arrived with an explicit company context
  // (e.g. "Build this" from the Roadmap, or the audit-upload flow), that is an
  // unambiguous signal to use Audit Mode — force it, overriding the default
  // Direct Mode the other effect below sets on initial mount before this runs.
  useEffect(() => {
    if (pageData?.companyId) {
      setSelCo(pageData.companyId);
      setDirectMode(false);
    }
  }, [pageData?.companyId]);

  // Auto-switch to direct mode only when there is genuinely no company context
  // anywhere — not on initial mount race, and not when navigation state already
  // specifies a company (that case is handled, and forced to Audit Mode, above).
  useEffect(() => {
    if (!selCo && !pageData?.companyId) setDirectMode(true);
    // Don't auto-switch back on manual company deselection — let user decide
  }, [selCo, pageData?.companyId]);

  // Load history when company changes. Keys PURELY on company selection
  // (selCo), NOT on directMode. This was briefly changed to also key on
  // directMode to fix a real "AI forgot everything" bug — but that bug's
  // actual root cause turned out to be unrelated (a React state-capture bug
  // in sendMessage, since fixed via messagesRef). Keying on directMode had a
  // serious side effect: since mode:'direct' is sent purely from the client
  // toggle, independent of which company is selected, it collapsed Company 1,
  // Company 2, and "no company" into the SAME shared history bucket any time
  // Direct Mode was active for any of them. Company selection alone should
  // determine which conversation you're looking at — Direct vs Audit mode is
  // just how THAT conversation gets framed, not a different conversation.
  useEffect(() => {
    setOrgStructure(null); // reset per company switch
    loadHistory(selCo || 'direct');
  }, [selCo]);

  // Show org-structure prompt when company is selected but chat is empty
  useEffect(() => {
    if (selCo && histLoaded && messages.length === 0) {
      setShowOrgPrompt(true);
    } else {
      setShowOrgPrompt(false);
    }
  }, [selCo, histLoaded, messages.length]);

  // Auto-fire autoPrompt — runs ONLY after history fully loaded (histLoaded=true),
  // eliminating the race where loadHistory's setMessages wiped the in-flight stream.
  // Keyed on a navigation nonce (_ts) so repeat clicks on the same task re-fire.
  useEffect(() => {
    if (!pageData?.autoPrompt || !selCo || !histLoaded) return;
    const fireKey = `${pageData._ts || ''}|${pageData.autoPrompt}`;
    if (autoFiredRef.current === fireKey) return; // already fired for this exact click
    autoFiredRef.current = fireKey;
    const prompt = pageData.autoPrompt;
    const t = setTimeout(() => {
      if (sendMsgRef.current) sendMsgRef.current(prompt);
    }, 150);
    return () => clearTimeout(t);
  }, [selCo, histLoaded, pageData?.autoPrompt, pageData?._ts]);

  // Scroll to bottom
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior:'smooth' });
  }, [messages]);

  // Token counting (debounced)
  useEffect(() => {
    if (!input.trim() || !selCo) { setTokenCount(null); return; }
    clearTimeout(tokenTimer.current);
    tokenTimer.current = setTimeout(async () => {
      const token = localStorage.getItem('token');
      try {
        const res = await fetch('/api/aistream/count-tokens', {
          method:'POST', headers:{'Content-Type':'application/json',Authorization:`Bearer ${token}`},
          body: JSON.stringify({ messages:[{role:'user',content:input}], company_id:selCo||null })
        });
        if (res.ok) { const d=await res.json(); setTokenCount(d.input_tokens); }
      } catch {}
    }, 900);
    return () => clearTimeout(tokenTimer.current);
  }, [input, selCo]);

  const loadHistory = async (cid) => {
    setHistLoading(true);
    setHistLoaded(false);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`/api/aistream/history/${cid}`, { headers:{ Authorization:`Bearer ${token}` }});
      if (res.ok) { const d=await res.json(); setMessages(Array.isArray(d)?d.map(m=>({...m,saved:true})):[]); }
      else setMessages([]);
    } catch { setMessages([]); }
    setHistLoading(false);
    setHistLoaded(true); // signals: safe to auto-fire now, no setMessages race possible
  };

  const toBase64 = f => new Promise((res,rej)=>{ const r=new FileReader(); r.onload=()=>res(r.result.split(',')[1]); r.onerror=rej; r.readAsDataURL(f); });

  const handleOrgStructure = async (file) => {
    setOrgStructure({ name: file.name, text: null, uploading: true });
    try {
      let text = '';
      if (file.type.startsWith('image/')) {
        // Image: send as base64 to the AI for OCR/description
        const b64 = await toBase64(file);
        text = `[Org chart image uploaded: ${file.name}]\n[Image data available for analysis]`;
        setOrgStructure({ name: file.name, text, b64, type: file.type, isImage: true, uploading: false });
      } else {
        // Text/CSV/JSON: read directly
        text = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result);
          r.onerror = rej;
          r.readAsText(file);
        });
        setOrgStructure({ name: file.name, text: text.slice(0, 8000), uploading: false });
      }
      showToast('Org structure loaded — Enpower AI will use this context', 'success');
      setShowOrgPrompt(false);
    } catch {
      setOrgStructure(null);
      showToast('Failed to read file', 'error');
    }
  };

  const MAX_FILE_BYTES  = 40 * 1024 * 1024;  // 40MB per file
  const MAX_TOTAL_BYTES = 40 * 1024 * 1024;  // 40MB aggregate across staged attachments —
  // every attachment type (including docx/xlsx's extracted text) eventually
  // gets embedded into the SAME outgoing JSON chat request body, so multiple
  // large files staged together could otherwise add up to far more than any
  // single-file check would catch. Matters more here than on most hosts since
  // this runs on a memory-constrained (1GB RAM) server.

  const handleFiles = async (files) => {
    for (const file of Array.from(files)) {
      const isImg = file.type.startsWith('image/');
      const isPDF = file.type==='application/pdf';
      const isTxt = file.type.startsWith('text/') || /\.(txt|csv|md|js|ts|py|json)$/.test(file.name);
      // .docx/.xlsx/.xls have no native Claude content-block type — there's no way
      // to send "raw docx bytes" for Claude to understand directly, unlike images
      // (vision) or PDFs (native document understanding). These need real text
      // extraction first. The server already has this fully working (mammoth for
      // docx, SheetJS for xlsx) via /upload-context — previously only reachable
      // from the "AI requested a document" flow, never from this main attach
      // button, so uploading an org chart or sales data here just got rejected
      // outright with an "unsupported" error.
      const isDoc = /\.(docx|xlsx|xls)$/i.test(file.name);
      if (!isImg&&!isPDF&&!isTxt&&!isDoc) { showToast(`${file.name}: unsupported`,'error'); continue; }
      if (file.size > MAX_FILE_BYTES) { showToast(`${file.name}: max 40MB per file`,'error'); continue; }
      const currentTotal = attachmentsRef.current.reduce((sum, a) => sum + (a.size || 0), 0);
      if (currentTotal + file.size > MAX_TOTAL_BYTES) {
        showToast(`${file.name}: would exceed 40MB total across attached files`, 'error');
        continue;
      }

      if (isDoc) {
        // Route through the existing server-side extraction endpoint instead
        // of trying to inline-base64 a format Claude can't natively read.
        try {
          const token = localStorage.getItem('token');
          const fd = new FormData();
          fd.append('file', file);
          const res = await fetch('/api/aistream/upload-context', {
            method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd
          });
          const data = await res.json();
          if (!res.ok || !data.success) { showToast(`${file.name}: ${data.error || 'failed to read file'}`, 'error'); continue; }
          const obj = { id:Date.now()+Math.random(), name:file.name, type:file.type, size:file.size, isDoc:true, extractedText: data.content };
          setAttachments(prev=>[...prev,obj]);
        } catch {
          showToast(`${file.name}: failed to read file`, 'error');
        }
        continue;
      }

      const b64 = await toBase64(file);
      const obj = { id:Date.now()+Math.random(), name:file.name, type:file.type, size:file.size, b64, isImg, isPDF, isTxt };
      if (isImg) obj.preview = URL.createObjectURL(file);
      setAttachments(prev=>[...prev,obj]);
    }
  };

  // Upload a file in response to an AI document request.
  // Parses the file server-side and auto-sends the content as context.
  const uploadContextFile = async (file, requestTitle) => {
    if (!file) return;
    showToast('Parsing file…', 'info');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('token');
      const res = await fetch('/api/aistream/upload-context', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      // Auto-send the content as a user message so the AI can use it immediately
      const contextMsg = data.contentType === 'image'
        ? `Here is the ${requestTitle} I uploaded (image):`
        : `Here is the ${requestTitle} I uploaded:\n\n` +
          `File: ${data.name} (${data.contentType})\n\n` +
          `${data.content.slice(0, 8000)}\n\n` +
          `Please continue with your analysis or artifact using this data.`;
      showToast(`${data.name} uploaded — sending to AI`, 'success');
      await sendMessage(contextMsg);
    } catch (e) {
      showToast('Upload failed: ' + e.message, 'error');
    }
  };

  const buildContent = (text, files) => {
    if (!files?.length) return text;
    const parts = [];
    // Proper UTF-8-safe decode — plain atob() treats each byte as Latin1,
    // which mangles any multi-byte character (non-English text, accents, etc.)
    const decodeUtf8 = b64 => new TextDecoder('utf-8').decode(Uint8Array.from(atob(b64), c => c.charCodeAt(0)));
    files.forEach(f=>{
      if (f.isImg) parts.push({type:'image',source:{type:'base64',media_type:f.type,data:f.b64}});
      else if (f.isPDF) parts.push({type:'document',source:{type:'base64',media_type:'application/pdf',data:f.b64},title:f.name});
      else if (f.isDoc) parts.push({type:'text',text:`[File: ${f.name}]\n${f.extractedText || ''}`});
      else parts.push({type:'text',text:`[File: ${f.name}]\n${decodeUtf8(f.b64)}`});
    });
    if (text.trim()) parts.push({type:'text',text});
    return parts;
  };

  /* ── Core send ─────────────────────────────────────────────────────────── */
  async function sendMessage(overrideText, useThinking = false) {
    sendMsgRef.current = null; // clear while running to prevent re-entry from ref
    const text = (overrideText || input).trim();
    const curAttach = attachmentsRef.current;
    setAttachments([]);
    if (!text && !curAttach.length) return;
    if (streamingRef.current) return;

    streamingRef.current = true;
    setStreaming(true);
    setCurrentTool(null);
    if (!overrideText) {
      setInput('');
      // Reset textarea height back to default after sending
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
    }
    setApiMissing(false);
    setTokenCount(null);

    const content    = buildContent(text, curAttach);
    const userMsg    = { role:'user', content, created_at:new Date().toISOString() };
    // messagesRef.current is synchronously up-to-date (kept in sync via the
    // useEffect above) — this is what fixes the actual root-cause bug. The
    // old pattern of capturing `prev` inside the setMessages updater and then
    // reading that capture on the very next line looked correct but wasn't:
    // React does not guarantee the updater runs before the next line executes.
    const currentMsgs = messagesRef.current;
    setMessages(prev => [...prev, userMsg]);

    const aId = `a${Date.now()}`;
    setMessages(prev => [...prev, { id:aId, role:'assistant', content:'', streaming:true, created_at:new Date().toISOString() }]);

    const token = localStorage.getItem('token');
    const controller = new AbortController();
    abortRef.current = controller;

    const cleanup = (removeMsg = false) => {
      if (removeMsg) setMessages(prev => prev.filter(m => m.id !== aId));
      else setMessages(prev => prev.map(m => m.id===aId ? {...m, streaming:false, toolStatus:null} : m));
      streamingRef.current = false;
      setStreaming(false);
      setCurrentTool(null);
      abortRef.current = null;
    };

    try {
      // Build history for the request. CRITICAL: an assistant turn that was
      // purely a tool call (e.g. "build me an excel" -> Claude just calls
      // create_excel with zero concluding prose) ends up with content:'' —
      // very common given the system prompt's "just build it, don't
      // over-explain" emphasis. Sending an empty string caused the server's
      // history sanitization to drop that message entirely, which cascaded
      // into ALSO dropping the user's preceding question (since the history
      // then incorrectly appeared to end in a dangling "user" turn) — wiping
      // out that whole exchange from context on the very next message. This
      // is why the AI "forgot" what it just built moments earlier. Fix:
      // synthesize a real summary from the artifacts when content is empty,
      // so tool-only turns are never invisible to history.
      const summarizeArtifacts = (m) => {
        // content can be an array (not a string) whenever a message included
        // a file/image attachment (see buildContent) — .trim() doesn't exist
        // on arrays and would throw here, silently aborting the send entirely
        // for any conversation where an earlier turn had an attachment.
        const textContent = typeof m.content === 'string'
          ? m.content
          : Array.isArray(m.content)
            ? m.content.filter(b => b.type === 'text').map(b => b.text || '').join('\n')
            : '';
        if (textContent && textContent.trim()) return textContent;
        if (m.artifacts && m.artifacts.length) {
          const typeLabels = {
            file: 'file', html_artifact: 'interactive app', code_result: 'calculation',
            search_result: 'web search', clarification: 'clarifying questions',
            esr_saved: 'engagement note', cad_artifact: 'CAD file'
          };
          const parts = m.artifacts.map(a => {
            const label = typeLabels[a.type] || 'artifact';
            const name  = a.title || a.filename || a.description || 'unnamed';
            return `${name} (${label})`;
          });
          return `I created: ${parts.join(', ')}.`;
        }
        // Attachment-only message with no text and no artifacts (e.g. just an
        // image with no caption) — still needs non-empty content or the
        // server's sanitization drops it (and can cascade into dropping the
        // adjacent turn too, per the fix above).
        if (Array.isArray(m.content) && m.content.some(b => b.type === 'image' || b.type === 'document')) {
          return '[Shared a file attachment]';
        }
        return textContent;
      };
      const history = currentMsgs.slice(-40).map(m => ({ role:m.role, content:summarizeArtifacts(m) }));

      const res = await fetch('/api/aistream/chat', {
        method:'POST',
        headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
        body: JSON.stringify({
          messages: [...history, {role:'user',content}],
          company_id: selCo||null,
          thinking: useThinking,
          mode: directMode ? 'direct' : 'auto',
          org_structure: orgStructure ? { name: orgStructure.name, text: orgStructure.text } : null,
        }),
        signal: controller.signal
      });

      if (!res.ok) {
        const err = await res.json().catch(()=>({}));
        if (err.error?.includes('ANTHROPIC_API_KEY')) setApiMissing(true);
        else showToast(err.error||'AI request failed','error');
        cleanup(true); return;
      }

      const reader  = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '', fullText = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream:true });
          const lines = buf.split('\n');
          buf = lines.pop();

          for (const line of lines) {
            if (line.startsWith(': ')) continue;
            if (!line.startsWith('data: ')) continue;
            try {
              const evt = JSON.parse(line.slice(6));
              if (evt.type === 'delta') {
                fullText += evt.text;
                setMessages(prev => prev.map(m => m.id===aId ? {...m, content:fullText} : m));

              } else if (evt.type === 'thinking_start') {
                setMessages(prev => prev.map(m => m.id===aId ? {...m, toolStatus:'🧠 Extended thinking…', steps:[...(m.steps||[]), {text:'🧠 Extended thinking activated…', ts:Date.now()}]} : m));
                setCurrentTool({ label:'Extended thinking', icon:'🧠', type:'thinking' });

              } else if (evt.type === 'thinking_done') {
                setMessages(prev => prev.map(m => m.id===aId ? {...m, toolStatus:null, thinkingDone:evt.summary} : m));
                setCurrentTool(null);

              } else if (evt.type === 'step') {
                // Collapsible step log — each step appended, shown in expandable "Working…" bar
                setMessages(prev => prev.map(m => m.id===aId
                  ? {...m, steps:[...(m.steps||[]), {text:evt.text, ts:Date.now()}]}
                  : m));

              } else if (evt.type === 'step_live') {
                // Live-updating line inside the Working dropdown — replaces itself as
                // text streams in, so expanding the dropdown mid-generation shows real
                // time text instead of a static chunk appearing after the fact.
                setMessages(prev => prev.map(m => m.id===aId ? {...m, liveStep: evt.text} : m));

              } else if (evt.type === 'step_live_end') {
                // Turn finished — the live line either became a permanent step
                // (narration) or its content is now flowing into the real answer
                // (final turn). Either way, clear the live-preview line.
                setMessages(prev => prev.map(m => m.id===aId ? {...m, liveStep: null} : m));

              } else if (evt.type === 'tool_start') {
                const label = evt.tool?.replace('create_','').replace(/_/g,' ');
                const statusText = `${evt.icon||'⚙️'} ${evt.tool === 'clarification_required' ? 'Preparing questions…' : 'Building ' + label + '…'}`;
                setMessages(prev => prev.map(m => m.id===aId ? {
                  ...m,
                  toolStatus: statusText,
                  steps:[...(m.steps||[]), {text:statusText, ts:Date.now()}]
                } : m));
                setCurrentTool({ label, icon:evt.icon||'⚙️', type:'tool' });

              } else if (evt.type === 'tool_executing') {
                const label = evt.tool?.replace('create_','').replace(/_/g,' ');
                setMessages(prev => prev.map(m => m.id===aId ? {...m, toolStatus:`${evt.icon||'⚙️'} Generating ${label}…`} : m));

              } else if (evt.type === 'artifact') {
                setMessages(prev => prev.map(m => m.id===aId ? {...m, artifacts:[...(m.artifacts||[]),evt.artifact], toolStatus:null} : m));
                setCurrentTool(null);

              } else if (evt.type === 'done') {
                setMessages(prev => prev.map(m => m.id===aId ? {...m, streaming:false, saved:true, toolStatus:null} : m));

              } else if (evt.type === 'error') {
                // Mark message as finished (not streaming) so UI doesn't hang
                setMessages(prev => prev.map(m => m.id===aId
                  ? { ...m, streaming:false, saved:true, toolStatus:null,
                      content: (m.content||'') + (m.content ? '\n\n' : '') + `⚠️ ${evt.error||'AI error — please try again.'}` }
                  : m));
                if (evt.error?.includes('ANTHROPIC_API_KEY')) setApiMissing(true);
                else showToast(evt.error||'AI error','error');
                cleanup(true); return;
              }
            } catch {}
          }
        }
      } finally {
        reader.releaseLock();
      }

      if (pageDataRef.current?.autoPrompt && pageDataRef.current?.companyId) {
        setShowBuiltBanner(true);
      }

    } catch (e) {
      if (e.name !== 'AbortError') showToast('Connection error — is the server running?','error');
      cleanup(true); return;
    }
    cleanup(false);
  }

  // Keep sendMsgRef pointing to latest sendMessage after every render
  sendMsgRef.current = sendMessage;

  const stop = () => { abortRef.current?.abort(); streamingRef.current=false; setStreaming(false); setCurrentTool(null); };

  const regenerate = () => {
    const lastUser = [...messages].reverse().find(m=>m.role==='user');
    if (!lastUser) return;
    setMessages(prev=>prev.slice(0,-1));
    const t = typeof lastUser.content==='string'?lastUser.content:lastUser.content?.find?.(c=>c.type==='text')?.text||'';
    setTimeout(()=>sendMessage(t),50);
  };

  const clearHistory = async () => {
    if (!window.confirm('Clear chat history?')) return;
    const token = localStorage.getItem('token');
    await fetch(`/api/aistream/history/${selCo || 'direct'}`,{method:'DELETE',headers:{Authorization:`Bearer ${token}`}});
    setMessages([]); showToast('History cleared');
  };

  const currentCo = companies.find(c=>c.id===selCo);
  const canSend   = (input.trim()||attachments.length>0) && !streaming && user?.credits>0;

  return (
    <div style={{display:'flex',height:'calc(100vh - 60px)',overflow:'hidden'}}>
      <div style={{flex:1,display:'flex',flexDirection:'column',minWidth:0}}>

        {/* ── Header ───────────────────────────────────────────────────── */}
        <div style={{padding:'8px 14px',borderBottom:'1px solid var(--border)',display:'flex',gap:8,alignItems:'center',flexShrink:0,background:'var(--bg-surface)',flexWrap:'wrap'}}>

          {/* Animated logo */}
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <div className={`chat-orb ${streaming ? (currentTool?.type==='thinking'?'thinking':'streaming') : 'idle'}`} style={{width:34,height:34}}>
              {streaming && currentTool?.type==='thinking' ? (
                <span style={{fontSize:13}}>🧠</span>
              ) : streaming ? (
                <div style={{width:12,height:12,border:'2px solid var(--gold)',borderTopColor:'transparent',borderRadius:'50%',animation:'orb-spin 0.8s linear infinite'}}/>
              ) : (
                <span style={{fontSize:13}}>✦</span>
              )}
            </div>
            <div>
              <div style={{fontFamily:'var(--ff-display)',fontSize:15,fontWeight:400,color:'var(--text)'}}>
                AI Workspace
              </div>
              {streaming && currentTool && (
                <div style={{fontSize:10,color:'var(--blue)',fontFamily:'var(--ff-mono)',animation:'tool-slide-in 0.2s ease both'}}>
                  {currentTool.icon} {currentTool.label}…
                </div>
              )}
              {!streaming && <div style={{fontSize:10,color:'var(--text3)',fontFamily:'var(--ff-mono)'}}>Enpower AI · Ready</div>}
            </div>
          </div>

          <select className="form-select" style={{maxWidth:'min(260px,100%)',fontSize:12}} value={selCo} onChange={e=>{setSelCo(e.target.value);}}>
            <option value="">No company context</option>
            {companies.map(c=><option key={c.id} value={c.id}>{c.name} ({c.response_count||0} responses)</option>)}
          </select>

          {/* Data Analysis button — proper component so useState hook is legal */}
          {selCo && currentCo && INDUSTRY_DATA_TEMPLATES[currentCo.industry] && (
            <DataAnalysisBtn
              industry={currentCo.industry}
              companyName={currentCo.name}
              showToast={showToast}
              sendMessage={sendMessage}
            />
          )}

          {selCo && currentCo && (
            <span style={{fontSize:11,padding:'3px 10px',background:'var(--indigo-s)',border:'1px solid var(--indigo-b)',borderRadius:20,color:'var(--blue)',fontFamily:'var(--ff-mono)'}}>
              🧬 {currentCo.name}
            </span>
          )}

          {/* Org structure chip — shows when uploaded */}
          {orgStructure && !orgStructure.uploading && (
            <div style={{display:'flex',alignItems:'center',gap:6,padding:'3px 10px',background:'var(--green-soft)',border:'1px solid var(--green-border)',borderRadius:20,fontSize:11,color:'var(--green)'}}>
              <span>🏢</span>
              <span style={{maxWidth:120,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{orgStructure.name}</span>
              <button onClick={()=>setOrgStructure(null)} style={{background:'none',border:'none',cursor:'pointer',color:'var(--green)',fontSize:14,lineHeight:1,padding:0,marginLeft:2}}>×</button>
            </div>
          )}
          {orgStructure?.uploading && (
            <span style={{fontSize:11,color:'var(--text-3)',display:'flex',alignItems:'center',gap:5}}>
              <span className="loader loader-sm"/>Reading…
            </span>
          )}

          <div style={{marginLeft:'auto',display:'flex',gap:8,alignItems:'center'}}>
            <span style={{padding:'3px 10px',background:'var(--gold-soft)',border:'1px solid var(--gold-border)',borderRadius:20,fontSize:11,color:'var(--blue)',fontFamily:'var(--ff-mono)',display:'flex',alignItems:'center',gap:4}}>
              <span>⚡</span><span>{user?.credits}</span>
            </span>
            {messages.length > 0 && <button className="btn btn-ghost btn-sm" onClick={clearHistory}>Clear</button>}
          </div>
        </div>

        {/* ── Global streaming progress bar ─────────────────────────── */}
        {streaming && (
          <div style={{height:2,background:'var(--bg4)',overflow:'hidden',flexShrink:0}}>
            <div style={{
              height:'100%',
              background:'linear-gradient(90deg, var(--gold) 0%, var(--gold-2) 50%, var(--gold) 100%)',
              backgroundSize:'200% 100%',
              animation:'progress-stripe 1.5s linear infinite',
              width:currentTool?'100%':'60%',
              transition:'width 0.5s ease',
            }}/>
          </div>
        )}

        {/* ── API key notice ─────────────────────────────────────────── */}
        {apiMissing && (
          <div style={{margin:'12px 20px 0',padding:'12px 16px',background:'rgba(192,57,77,0.08)',border:'1px solid rgba(192,57,77,0.3)',borderRadius:8,display:'flex',gap:12,alignItems:'flex-start'}}>
            <span style={{fontSize:16}}>⊗</span>
            <div style={{flex:1}}>
              <div style={{fontWeight:600,color:'var(--rose)',marginBottom:3,fontSize:13}}>ANTHROPIC_API_KEY not configured</div>
              <div style={{fontSize:12,color:'var(--text2)'}}>Add <code style={{fontFamily:'var(--ff-mono)',fontSize:11,background:'var(--bg-elevated)',padding:'1px 5px',borderRadius:4}}>ANTHROPIC_API_KEY=sk-ant-...</code> to <code style={{fontFamily:'var(--ff-mono)',fontSize:11,background:'var(--bg-elevated)',padding:'1px 5px',borderRadius:4}}>server/.env</code></div>
            </div>
            <button onClick={()=>setApiMissing(false)} style={{background:'none',border:'none',cursor:'pointer',color:'var(--text3)',fontSize:18}}>×</button>
          </div>
        )}

        {/* ── Org structure nudge ──────────────────────────────────────── */}
        {showOrgPrompt && selCo && !orgStructure && (
          <div style={{margin:'10px 20px 0',padding:'14px 18px',background:'var(--blue-soft)',border:'1px solid var(--blue-border)',borderRadius:10,display:'flex',gap:14,alignItems:'flex-start',flexWrap:'wrap'}}>
            <span style={{fontSize:22,flexShrink:0}}>🏢</span>
            <div style={{flex:1}}>
              <div style={{fontWeight:700,fontSize:13.5,color:'var(--blue)',marginBottom:4}}>Upload your org structure for tailored artifacts</div>
              <div style={{fontSize:13,color:'var(--text-2)',lineHeight:1.6,marginBottom:10}}>
                Enpower AI will use your org chart to generate role-specific RACIs, KPI owners, SOPs, and org designs — instead of generic placeholders.
                Accepted: org chart image, CSV (name, role, manager), or plain text hierarchy.
              </div>
              <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
                <label style={{display:'inline-flex',alignItems:'center',gap:6,padding:'7px 14px',background:'var(--blue)',color:'#fff',borderRadius:7,cursor:'pointer',fontSize:13,fontWeight:600}}>
                  <span>📎</span> Upload org structure
                  <input type="file" accept="image/*,.csv,.txt,.json" style={{display:'none'}}
                    onChange={e => e.target.files[0] && handleOrgStructure(e.target.files[0])} />
                </label>
                <button onClick={()=>setShowOrgPrompt(false)} style={{background:'none',border:'none',cursor:'pointer',fontSize:12,color:'var(--text-3)',textDecoration:'underline',fontFamily:'inherit'}}>
                  Skip — use generic roles
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Built banner ───────────────────────────────────────────── */}
        {showBuiltBanner && pageData?.autoPrompt && (
          <div style={{margin:'10px 20px 0',padding:'12px 16px',background:'rgba(46,125,92,0.08)',border:'1px solid rgba(46,125,92,0.25)',borderRadius:8,display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
            <span>✅</span>
            <div style={{flex:1}}>
              <div style={{fontWeight:600,color:'var(--sage-2)',fontSize:13,marginBottom:2}}>Artifact ready!</div>
              <div style={{fontSize:12,color:'var(--text2)'}}>Mark this roadmap item as done.</div>
            </div>
            <div style={{display:'flex',gap:8}}>
              <button className="btn btn-ghost btn-sm" style={{fontSize:11,color:'var(--sage-2)'}} onClick={()=>navigate('/report', { state: {companyId:pageData.companyId} })}>View Roadmap →</button>
              <button onClick={()=>setShowBuiltBanner(false)} style={{background:'none',border:'none',cursor:'pointer',color:'var(--text3)',fontSize:16}}>×</button>
            </div>
          </div>
        )}

        {/* ── Messages ───────────────────────────────────────────────── */}
        <div style={{flex:1,overflowY:'auto',padding:'24px 24px 0'}}>
          {/* Direct Mode banner */}
          {directMode && (
            <div style={{
              display:'flex', alignItems:'center', gap:10, padding:'10px 16px',
              background:'rgba(22,163,74,0.08)', borderBottom:'1px solid rgba(22,163,74,0.2)',
              fontSize:13, color:'var(--green)', flexShrink:0,
            }}>
              <span style={{fontSize:16}}>🎯</span>
              <span>
                <strong>Direct Mode</strong> — Describe your business problem directly.
                The AI will ask 1–2 questions then solve it using the Enpower methodology.
                Your audit scores are not being used in this mode.
              </span>
              <button onClick={() => setDirectMode(false)}
                style={{ marginLeft:'auto', fontSize:11, color:'var(--text-4)', background:'none',
                  border:'none', cursor:'pointer', textDecoration:'underline', flexShrink:0 }}>
                Switch to Audit Mode
              </button>
            </div>
          )}

          {histLoading && (
            <div className="page-loading">
              <div className="loader"/>
              <div className="page-loading-text">Loading conversation…</div>
            </div>
          )}

          {!histLoading && messages.length === 0 && (
            <div style={{maxWidth:700,margin:'32px auto 0',paddingBottom:32}}>
              {/* Welcome header */}
              <div style={{textAlign:'center',marginBottom:28}}>
                <div style={{width:56,height:56,margin:'0 auto 16px',borderRadius:'50%',background:'linear-gradient(135deg,var(--navy-4),var(--navy-5))',border:'1px solid var(--gold-border)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:24,boxShadow:'var(--shadow-glow)'}}>✦</div>
                <div style={{fontFamily:'var(--ff-display)',fontSize:24,fontWeight:400,marginBottom:8}}>
                  {selCo ? `Working with ${currentCo?.name}` : 'Enpower AI Workspace'}
                </div>
                <div style={{color:'var(--text2)',fontSize:13,lineHeight:1.65,maxWidth:440,margin:'0 auto 16px'}}>
                  {selCo
                    ? `Diagnostic data loaded. Ask a question, launch a playbook, or request any artifact.`
                    : 'Select a company above for context-aware consulting, or ask any org design question.'}
                </div>
                {selCo && (
                  <button
                    onClick={() => navigate('/artifacts', { state: { companyId: selCo } })}
                    style={{ fontSize:11, color:'var(--text-3)', background:'transparent', border:'1px solid var(--border)', borderRadius:6, padding:'4px 12px', cursor:'pointer' }}>
                    🗂 View all generated artifacts →
                  </button>
                )}
              </div>
              {/* Industry Data Analysis section — only shown when company has a known industry */}
              {/* Industry Data Analysis — proper component, no IIFE */}
              {selCo && currentCo && INDUSTRY_DATA_TEMPLATES[currentCo?.industry] && (
                <IndustryDataCards
                  industry={currentCo.industry}
                  companyName={currentCo.name}
                  showToast={showToast}
                  sendMessage={sendMessage}
                />
              )}
              {/* Playbooks section */}
              <div style={{marginBottom:28}}>
                <div style={{fontSize:10,fontWeight:700,letterSpacing:'0.1em',color:'var(--text-3)',textTransform:'uppercase',marginBottom:12}}>
                  Solution Playbooks — complete multi-artifact executions
                </div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:10}}>
                  {PLAYBOOKS.map(pb => (
                    <button key={pb.id}
                      onClick={() => {
                        const co = currentCo;
                        const prompt = pb.prompt
                          .replace(/{company}/g, co?.name || 'this company')
                          .replace(/{industry}/g, co?.industry || 'your industry')
                          .replace(/{size}/g, co?.size || 'your team size');
                        sendMessage(prompt);
                      }}
                      style={{
                        textAlign:'left', padding:'12px 14px', borderRadius:10, cursor:'pointer',
                        border:`1px solid ${pb.color}30`, background:`${pb.color}08`,
                        transition:'all 0.15s',
                      }}>
                      <div style={{fontSize:22,marginBottom:6}}>{pb.icon}</div>
                      <div style={{fontSize:13,fontWeight:700,color:'var(--text)',marginBottom:4}}>{pb.title}</div>
                      <div style={{fontSize:11.5,color:'var(--text-3)',lineHeight:1.4}}>{pb.desc}</div>
                      <div style={{marginTop:8,fontSize:11,color:pb.color,fontWeight:600}}>▶ Launch full playbook →</div>
                    </button>
                  ))}
                </div>
              </div>
              {/* Quick suggestions */}
              <div style={{marginBottom:8,fontSize:10,fontWeight:700,letterSpacing:'0.1em',color:'var(--text-3)',textTransform:'uppercase'}}>Quick questions</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',gap:8}}>
                {SUGGESTIONS.map(s => (
                  <button key={s.text} className="suggestion-chip" onClick={()=>sendMessage(s.text)}>
                    <span style={{marginRight:6}}>{s.icon}</span>{s.text}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <MsgBubble key={msg.id||i} msg={msg} isLast={i===messages.length-1}
              isStreaming={streaming}
              onCopy={t=>{navigator.clipboard.writeText(t);showToast('Copied');}}
              onRegenerate={regenerate}
              onFollowUp={t=>sendMessage(t)}
              onPreview={html=>setArtifactPanel(html)}
              onUploadContext={uploadContextFile}/>
          ))}
          <div ref={endRef} style={{height:24}}/>
        </div>

        {/* ── Input area ─────────────────────────────────────────────── */}
        <div style={{padding:'10px 14px 14px',flexShrink:0}}>
          {attachments.length > 0 && (
            <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:10}}>
              {attachments.map(f => <AttachChip key={f.id} file={f} onRemove={()=>setAttachments(prev=>prev.filter(a=>a.id!==f.id))}/>)}
            </div>
          )}

          <div className={`input-area ${streaming ? 'streaming-active' : ''}`}>
            <textarea
              ref={textareaRef}
              value={input}
              onChange={e=>{
                setInput(e.target.value);
                // Auto-grow: reset then set to scrollHeight, capped at max-height (CSS handles overflow scroll)
                const el = e.target;
                el.style.height = 'auto';
                el.style.height = Math.min(el.scrollHeight, 240) + 'px';
              }}
              onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();sendMessage();}}}
              placeholder={
                streaming ? 'Enpower AI is working…' :
                user?.credits===0 ? 'No credits remaining — upgrade to continue' :
                selCo ? 'Ask anything, or request an artifact…' :
                'Select a company for full context, or ask a general question…'
              }
              disabled={streaming || user?.credits===0}
              rows={2}
              style={{background:'transparent',border:'none',outline:'none',color:streaming?'var(--text3)':'var(--text)',fontSize:14,resize:'none',fontFamily:'var(--ff-body)',lineHeight:1.6,width:'100%',cursor:streaming?'not-allowed':'text',minHeight:44,maxHeight:240,overflowY:'auto',transition:'height 0.1s ease'}}
              onDrop={e=>{e.preventDefault();handleFiles(e.dataTransfer.files);}}
              onDragOver={e=>e.preventDefault()}
            />

            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{display:'flex',gap:8,alignItems:'center'}}>
                <input ref={fileRef} type="file" multiple accept="image/*,.pdf,.txt,.csv,.md,.js,.ts,.py,.json,.docx,.xlsx,.xls" style={{display:'none'}} onChange={e=>handleFiles(e.target.files)}/>
                <button onClick={()=>!streaming&&fileRef.current?.click()} disabled={streaming}
                  style={{background:'none',border:'1px solid var(--border2)',borderRadius:7,padding:'5px 11px',cursor:streaming?'not-allowed':'pointer',color:streaming?'var(--text4)':'var(--text3)',fontSize:12,display:'flex',alignItems:'center',gap:5,transition:'all 0.15s',opacity:streaming?0.5:1}}
                  onMouseEnter={e=>{if(!streaming){e.currentTarget.style.color='var(--gold-2)';e.currentTarget.style.borderColor='var(--gold-border)';}}}
                  onMouseLeave={e=>{e.currentTarget.style.color='var(--text3)';e.currentTarget.style.borderColor='var(--border2)';}}>
                  📎 Attach
                </button>
                {tokenCount && !streaming && (
                  <span style={{fontSize:10,color:'var(--text4)',fontFamily:'var(--ff-mono)'}}>~{tokenCount.toLocaleString()} tokens</span>
                )}
              </div>

              <div style={{display:'flex',gap:8,alignItems:'center'}}>
                {/* Direct Mode toggle — switches between audit-driven and problem-driven modes */}
                <button
                  onClick={() => setDirectMode(d => !d)}
                  title={directMode
                    ? 'Direct Mode ON — AI is treating your message as a standalone business problem (click to switch back to audit mode)'
                    : 'Audit Mode — AI is using your diagnostic scores as context (click to switch to Direct Mode if you have a specific problem)'}
                  style={{
                    padding:'5px 12px', borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer',
                    border:`1px solid ${directMode ? 'rgba(22,163,74,0.5)' : 'var(--border)'}`,
                    background: directMode ? 'rgba(22,163,74,0.1)' : 'var(--surface)',
                    color: directMode ? 'var(--green)' : 'var(--text-3)',
                    transition:'all 0.15s', whiteSpace:'nowrap',
                  }}>
                  {directMode ? '🎯 Direct' : '📊 Audit'}
                </button>
                {!streaming && (
                  <button className="think-btn" disabled={!canSend}
                    onClick={()=>sendMessage(undefined,true)}
                    title="Extended thinking — Enpower AI reasons deeply before answering">
                    🧠 Deep
                  </button>
                )}
                {streaming ? (
                  <button className="send-btn streaming" onClick={stop}>
                    <svg width="10" height="10" viewBox="0 0 10 10"><rect width="10" height="10" rx="2" fill="currentColor"/></svg>
                    Stop
                  </button>
                ) : (
                  <button className={`send-btn ${canSend?'ready':'disabled'}`}
                    disabled={!canSend} onClick={()=>sendMessage()}>
                    Send
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Artifact side panel ───────────────────────────────────────── */}
      {artifactPanel && <ArtifactPanel html={artifactPanel} onClose={()=>setArtifactPanel(null)}/>}
    </div>
  );
}
